import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useTrialCountdown } from '@/hooks/useTrialCountdown';
import { useTrialWriteBlocked } from '@/hooks/useTrialWriteAccess';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

const SUBSCRIPTION_PATH = '/profile?tab=billing';

function formatDate(iso: string | undefined, lang: 'it' | 'en'): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Persistent banner while freemium trial is expired — read-only mode, upgrade CTA. */
export function TrialExpiredBanner() {
  const writeBlocked = useTrialWriteBlocked();
  const { data: trial } = useTrialCountdown();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  if (!writeBlocked) return null;

  const dateStr = formatDate(trial?.active ? trial.endsAt : undefined, language);
  const message = dateStr
    ? t('trial.expired.banner.withDate', { date: dateStr })
    : t('trial.expired.banner.message');

  const handleClick = () => navigate(SUBSCRIPTION_PATH);

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={handleClick}
      className={cn(
        'border-b cursor-pointer transition-colors hover:brightness-95 shrink-0',
        'bg-destructive/10 text-destructive border-destructive/30',
      )}
    >
      <div className="max-w-screen-2xl mx-auto w-full px-4 md:px-6 py-2 flex items-center gap-3">
        <Lock className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium flex-1 min-w-0">{message}</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleClick(); }}
          className="text-xs font-semibold px-3 py-1 rounded-md shrink-0 bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
        >
          {t('trial.expired.cta')}
        </button>
      </div>
    </div>
  );
}

export default TrialExpiredBanner;
